import React from "react";
import { useI18n } from "@/i18n";
import NewsletterForm from "@/components/home/NewsletterForm";
import {
  ShieldCheck,
  TrendingUp,
  Users,
  CheckCircle2,
  BadgeCheck,
  Lock,
  Award,
  Star,
  MapPin,
  Wallet,
  Target,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const SERVICE_ICONS = [ShieldCheck, TrendingUp, Users];
const ELIG_ICONS = [MapPin, Wallet, Target];
const TRUST_ICONS = [BadgeCheck, Lock, Award, Star];

export default function Home() {
  const { t, tRaw } = useI18n();
  const services = tRaw("services.cards") || [];
  const stats = tRaw("stats.items") || [];
  const steps = tRaw("eligibility.steps") || [];
  const processItems = tRaw("process.items") || [];
  const testimonials = tRaw("testimonials.items") || [];
  const faqItems = tRaw("faq.items") || [];
  const trustBadges = [
    t("hero.trust.badge1"),
    t("hero.trust.badge2"),
    t("hero.trust.badge3"),
    t("hero.trust.badge4"),
  ];

  const scrollToNewsletter = () => {
    document
      .getElementById("newsletter")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main data-testid="home-page" className="bg-[#FAF9F5]">
      {/* Announcement ribbon */}
      <div
        data-testid="announcement-ribbon"
        className="bg-[#064E3B] text-white text-sm py-2.5 px-4 text-center"
      >
        <span className="font-medium">{t("announcement")}</span>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <span
              data-testid="hero-eyebrow"
              className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-md"
            >
              {t("hero.eyebrow")}
            </span>
            <h1
              data-testid="hero-title"
              className="mt-4 font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 leading-tight"
            >
              {t("hero.title")}
            </h1>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
              {t("hero.subtitle")}
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3">
              <button
                data-testid="hero-cta-button"
                onClick={scrollToNewsletter}
                className="inline-flex items-center justify-center bg-[#0F2C59] hover:bg-[#153a75] text-white font-semibold px-6 py-3 rounded-md text-sm"
              >
                {t("hero.primaryCta")}
              </button>
              <button
                data-testid="hero-secondary-cta"
                onClick={() =>
                  document
                    .getElementById("services")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
                className="inline-flex items-center justify-center border border-slate-300 text-slate-800 font-semibold px-6 py-3 rounded-md text-sm hover:border-slate-500 hover:bg-white"
              >
                {t("hero.secondaryCta")}
              </button>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-3">
              {trustBadges.map((label, i) => {
                const Icon = TRUST_ICONS[i];
                return (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-xs sm:text-sm text-slate-600"
                  >
                    <Icon size={16} className="text-[#0F2C59] shrink-0" />
                    <span>{label}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Hero image with badge */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl border-4 border-white bg-slate-100">
              <img
                src="/images/hero-advisors.jpg"
                alt={t("hero.imageAlt")}
                data-testid="hero-image"
                className="w-full h-[420px] lg:h-[520px] object-cover"
                loading="eager"
              />
            </div>
            <div className="hidden md:flex absolute -bottom-6 -left-6 bg-white shadow-xl rounded-xl p-4 items-center gap-3 border border-slate-200 max-w-[260px]">
              <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-emerald-100 text-emerald-800">
                <CheckCircle2 size={22} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
                  {t("stats.title")}
                </p>
                <p className="text-lg font-semibold text-slate-900">
                  {stats[0]?.value} <span className="text-emerald-700">+</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((s, i) => (
            <div key={i} className="text-center">
              <p className="font-serif text-2xl sm:text-3xl font-bold text-[#0F2C59]">
                {s.value}
              </p>
              <p className="mt-1 text-xs sm:text-sm text-slate-600 uppercase tracking-wider">
                {s.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-md">
              {t("services.eyebrow")}
            </span>
            <h2
              data-testid="services-title"
              className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900"
            >
              {t("services.title")}
            </h2>
            <p className="mt-3 text-slate-600 text-base lg:text-lg">
              {t("services.subtitle")}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {services.map((card, i) => {
              const Icon = SERVICE_ICONS[i] || ShieldCheck;
              return (
                <article
                  key={i}
                  data-testid={`service-card-${i}`}
                  className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-md hover:border-[#0F2C59]/40 transition-shadow"
                >
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-[#0F2C59] text-white">
                    <Icon size={22} />
                  </span>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">
                    {card.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                    {card.description}
                  </p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ELIGIBILITY */}
      <section id="eligibility" className="py-16 lg:py-24 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-white">
            <img
              src="/images/consultation.jpg"
              alt=""
              className="w-full h-[420px] object-cover"
            />
          </div>
          <div>
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-md">
              {t("eligibility.eyebrow")}
            </span>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              {t("eligibility.title")}
            </h2>
            <p className="mt-3 text-slate-600 text-base">
              {t("eligibility.subtitle")}
            </p>
            <ol className="mt-6 space-y-4">
              {steps.map((step, i) => {
                const Icon = ELIG_ICONS[i] || CheckCircle2;
                return (
                  <li
                    key={i}
                    data-testid={`eligibility-step-${i + 1}`}
                    className="flex gap-4 bg-[#FAF9F5] p-4 rounded-lg border border-slate-200"
                  >
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-[#064E3B] text-white shrink-0">
                      <Icon size={20} />
                    </span>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-[#C5A059] font-bold">
                        {step.step}
                      </p>
                      <p className="text-slate-900 font-semibold">
                        {step.title}
                      </p>
                      <p className="text-sm text-slate-600 mt-0.5">
                        {step.description}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
            <button
              data-testid="eligibility-cta"
              onClick={scrollToNewsletter}
              className="mt-7 inline-flex items-center bg-[#0F2C59] hover:bg-[#153a75] text-white font-semibold px-6 py-3 rounded-md text-sm"
            >
              {t("eligibility.cta")}
            </button>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-md">
              {t("process.eyebrow")}
            </span>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
              {t("process.title")}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {processItems.map((it, i) => (
              <div
                key={i}
                className="p-6 bg-white rounded-xl border border-slate-200"
              >
                <p className="font-serif text-3xl text-[#C5A059] font-bold">
                  0{i + 1}
                </p>
                <h4 className="mt-2 font-semibold text-slate-900">
                  {it.title}
                </h4>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {it.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 lg:py-24 bg-[#0F2C59] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-[#C5A059] bg-white/10 px-3 py-1 rounded-md">
              {t("testimonials.eyebrow")}
            </span>
            <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
              {t("testimonials.title")}
            </h2>
          </div>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((it, i) => (
              <blockquote
                key={i}
                className="bg-white/5 border border-white/10 rounded-2xl p-6"
              >
                <p className="text-sm text-slate-100 leading-relaxed">
                  “{it.quote}”
                </p>
                <div className="mt-5 pt-4 border-t border-white/10">
                  <p className="font-semibold">{it.name}</p>
                  <p className="text-xs text-slate-300">{it.role}</p>
                </div>
              </blockquote>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="newsletter" className="py-16 lg:py-24 bg-[#FAF9F5]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2">
            <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-md">
              {t("newsletter.eyebrow")}
            </span>
            <h2
              data-testid="newsletter-title"
              className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900"
            >
              {t("newsletter.title")}
            </h2>
            <p className="mt-3 text-slate-600 text-base leading-relaxed">
              {t("newsletter.subtitle")}
            </p>
            <img
              src="/images/family-support.jpg"
              alt=""
              className="mt-6 rounded-xl w-full h-56 object-cover hidden lg:block"
            />
          </div>
          <div className="lg:col-span-3">
            <NewsletterForm />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 lg:py-24 bg-white border-t border-slate-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-800 bg-emerald-100 px-3 py-1 rounded-md">
            {t("faq.eyebrow")}
          </span>
          <h2 className="mt-4 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
            {t("faq.title")}
          </h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="faq-accordion">
            {faqItems.map((item, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                data-testid={`faq-item-${i}`}
                className="border-b border-slate-200"
              >
                <AccordionTrigger className="text-left text-slate-900 font-semibold hover:no-underline">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 bg-[#064E3B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            {t("cta.title")}
          </h2>
          <p className="mt-3 text-slate-100 text-base max-w-2xl mx-auto">
            {t("cta.subtitle")}
          </p>
          <button
            data-testid="final-cta-button"
            onClick={scrollToNewsletter}
            className="mt-6 inline-flex items-center bg-[#C5A059] hover:bg-[#d4b56d] text-[#0F2C59] font-bold px-8 py-3 rounded-md text-sm tracking-wide"
          >
            {t("cta.button")}
          </button>
        </div>
      </section>
    </main>
  );
}
